import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { theme } from '../../styles/theme';

const PreviewContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const TabBar = styled.div`
  display: flex;
  gap: ${theme.spacing.xs};
  margin-bottom: ${theme.spacing.lg};
  border-bottom: 2px solid ${theme.colors.borderLight};
`;

const Tab = styled.button<{ active: boolean }>`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: none;
  border: none;
  border-bottom: 3px solid ${props => props.active ? theme.colors.primary : 'transparent'};
  color: ${props => props.active ? theme.colors.primary : theme.colors.text.secondary};
  font-weight: ${props => props.active ? '600' : '500'};
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: -2px;

  &:hover {
    color: ${theme.colors.primary};
  }
`;

const ContentArea = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const ExamplePrompt = styled.div`
  background: ${theme.colors.backgroundSecondary};
  padding: ${theme.spacing.md};
  border-radius: ${theme.radii.md};
  margin-bottom: ${theme.spacing.md};
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};

  strong {
    color: ${theme.colors.text.primary};
  }
`;

const TemplateDemo = styled(motion.div)`
  background: linear-gradient(135deg, 
    ${theme.colors.primaryLight}10, 
    ${theme.colors.primary}10
  );
  padding: ${theme.spacing.lg};
  border-radius: ${theme.radii.md};
  margin-bottom: ${theme.spacing.md};
  line-height: 1.8;
`;

const Blank = styled.span`
  background: ${theme.colors.primary};
  color: white;
  padding: 2px 10px;
  border-radius: ${theme.radii.sm};
  margin: 0 4px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-block;
  position: relative;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
  }
`;

const Dropdown = styled(motion.div)`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 4px;
  background: white;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  box-shadow: ${theme.shadows.lg};
  z-index: 10;
  min-width: 200px;
`;

const Option = styled.div`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  cursor: pointer;
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.primary};
  font-weight: 400;

  &:hover {
    background: ${theme.colors.primaryLight};
  }

  &:first-child {
    border-radius: ${theme.radii.md} ${theme.radii.md} 0 0;
  }

  &:last-child {
    border-radius: 0 0 ${theme.radii.md} ${theme.radii.md};
  }
`;

const CTAButton = styled.button`
  width: 100%;
  padding: ${theme.spacing.md};
  background: linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.primaryDark});
  color: white;
  border: none;
  border-radius: ${theme.radii.md};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(102, 126, 234, 0.3);
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: ${theme.spacing.md} 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: ${theme.spacing.sm};
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};

  &::before {
    content: '✨';
    margin-right: ${theme.spacing.sm};
  }
`;

interface BlankData {
  id: string;
  value: string;
  options: string[];
}

export const FossilFillsPreview: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'demo' | 'features'>('demo');
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [blanks, setBlanks] = useState<Record<string, string>>({
    role: 'screenwriter',
    genre: 'thriller',
    format: 'scene',
    emotion: 'tension'
  });

  const blankOptions: Record<string, string[]> = {
    role: ['screenwriter', 'novelist', 'playwright', 'director'],
    genre: ['thriller', 'drama', 'comedy', 'sci-fi'],
    format: ['scene', 'dialogue', 'treatment', 'synopsis'],
    emotion: ['tension', 'humor', 'romance', 'suspense']
  };

  const handleBlankClick = (blankId: string) => {
    setActiveDropdown(activeDropdown === blankId ? null : blankId);
  };

  const handleOptionSelect = (blankId: string, value: string) => {
    setBlanks(prev => ({ ...prev, [blankId]: value }));
    setActiveDropdown(null);
  };

  const handleTryIt = () => {
    navigate('/fossil_fills');
  };

  return (
    <PreviewContainer>
      <TabBar>
        <Tab 
          active={activeTab === 'demo'} 
          onClick={() => setActiveTab('demo')}
        >
          Interactive Demo
        </Tab>
        <Tab 
          active={activeTab === 'features'} 
          onClick={() => setActiveTab('features')}
        >
          Features
        </Tab>
      </TabBar>

      <ContentArea>
        <AnimatePresence mode="wait">
          {activeTab === 'demo' ? (
            <motion.div
              key="demo"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <ExamplePrompt>
                <strong>Your prompt:</strong> "Write a scene"
              </ExamplePrompt>

              <TemplateDemo>
                <div style={{ marginBottom: theme.spacing.sm, fontSize: theme.fontSizes.xs, color: theme.colors.text.secondary }}>
                  🦖 Click the blanks to customize:
                </div>
                You are an{' '}
                <span style={{ position: 'relative' }}>
                  <Blank onClick={() => handleBlankClick('role')}>
                    {blanks.role}
                  </Blank>
                  <AnimatePresence>
                    {activeDropdown === 'role' && (
                      <Dropdown
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                      >
                        {blankOptions.role.map(option => (
                          <Option 
                            key={option}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOptionSelect('role', option);
                            }}
                          >
                            {option}
                          </Option>
                        ))}
                      </Dropdown>
                    )}
                  </AnimatePresence>
                </span>
                {' '}specializing in{' '}
                <span style={{ position: 'relative' }}>
                  <Blank onClick={() => handleBlankClick('genre')}>
                    {blanks.genre}
                  </Blank>
                  <AnimatePresence>
                    {activeDropdown === 'genre' && (
                      <Dropdown
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                      >
                        {blankOptions.genre.map(option => (
                          <Option 
                            key={option}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOptionSelect('genre', option);
                            }}
                          >
                            {option}
                          </Option>
                        ))}
                      </Dropdown>
                    )}
                  </AnimatePresence>
                </span>
                . Create a{' '}
                <span style={{ position: 'relative' }}>
                  <Blank onClick={() => handleBlankClick('format')}>
                    {blanks.format}
                  </Blank>
                  <AnimatePresence>
                    {activeDropdown === 'format' && (
                      <Dropdown
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                      >
                        {blankOptions.format.map(option => (
                          <Option 
                            key={option}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOptionSelect('format', option);
                            }}
                          >
                            {option}
                          </Option>
                        ))}
                      </Dropdown>
                    )}
                  </AnimatePresence>
                </span>
                {' '}with{' '}
                <span style={{ position: 'relative' }}>
                  <Blank onClick={() => handleBlankClick('emotion')}>
                    {blanks.emotion}
                  </Blank>
                  <AnimatePresence>
                    {activeDropdown === 'emotion' && (
                      <Dropdown
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                      >
                        {blankOptions.emotion.map(option => (
                          <Option 
                            key={option}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOptionSelect('emotion', option);
                            }}
                          >
                            {option}
                          </Option>
                        ))}
                      </Dropdown>
                    )}
                  </AnimatePresence>
                </span>
                ...
              </TemplateDemo>

              <CTAButton onClick={handleTryIt}>
                Try Full Fossil Fills Experience →
              </CTAButton>
            </motion.div>
          ) : (
            <motion.div
              key="features"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <FeatureList>
                <FeatureItem>
                  Mad Libs-style template system
                </FeatureItem>
                <FeatureItem>
                  Domain-specific suggestions
                </FeatureItem>
                <FeatureItem>
                  12+ blanks for rich prompts
                </FeatureItem>
                <FeatureItem>
                  Educational insights included
                </FeatureItem>
                <FeatureItem>
                  Templates for screenwriting, coding, marketing
                </FeatureItem>
                <FeatureItem>
                  Web-based template editor
                </FeatureItem>
                <FeatureItem>
                  Export improved prompts
                </FeatureItem>
              </FeatureList>

              <CTAButton onClick={handleTryIt}>
                Start Using Fossil Fills →
              </CTAButton>
            </motion.div>
          )}
        </AnimatePresence>
      </ContentArea>
    </PreviewContainer>
  );
};